import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, ExternalLink, Trash2, Plus } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import ImageUpload from "../../components/ImageUpload";
import { PromoEditor } from "./PromoEditor";
import { UpgradeLock } from "../../components/UpgradeLock";
import { DesignEditor } from "./DesignEditor";
import { usePlan } from "../../context/PlanContext";

export default function StorefrontTab() {
  const { has } = usePlan();
  const [biz, setBiz] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/business/me").then(({ data }) => setBiz(data)); }, []);
  if (!biz) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const set = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/business/me", {
        name: biz.name, slug: biz.slug, logo_url: biz.logo_url, hero_image: biz.hero_image,
        brand_color: biz.brand_color, text_color: biz.text_color, button_color: biz.button_color,
        button_text_color: biz.button_text_color, description: biz.description, terms: biz.terms,
        voucher_validity_months: parseInt(biz.voucher_validity_months ?? 12),
        seo_title: biz.seo_title || "", seo_description: biz.seo_description || "", share_image: biz.share_image || "",
        website: biz.website || "", social_links: biz.social_links || [], promo_banner: biz.promo_banner || {}, video_messages_enabled: biz.video_messages_enabled !== false,
        ...(biz.design && has("advanced_design") ? { design: biz.design } : {}),
      });
      setBiz(data);
      toast({ title: "Storefront updated" });
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] space-y-4">
        <Field label="Business name" value={biz.name} onChange={set("name")} />
        <Field label="Store URL slug" value={biz.slug} onChange={set("slug")} prefix="/store/" />
        <ImageUpload label="Logo" value={biz.logo_url} onChange={(v) => setBiz({ ...biz, logo_url: v })} />
        <ImageUpload label="Hero image" value={biz.hero_image} onChange={(v) => setBiz({ ...biz, hero_image: v })} />
        <div>
          <label className="block text-sm font-medium text-[#16243b] mb-1.5">Brand colour <span className="text-xs text-[#8a97a8] font-normal">(hero background)</span></label>
          <div className="flex items-center gap-3">
            <input type="color" value={biz.brand_color || "#16243b"} onChange={set("brand_color")} className="w-12 h-10 rounded cursor-pointer border border-gray-200" />
            <input value={biz.brand_color || ""} onChange={set("brand_color")} className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            ["Text colour", "text_color", "#16243b"],
            ["Button colour", "button_color", "#2ec9c9"],
            ["Button text colour", "button_text_color", "#ffffff"],
          ].map(([label, key, def]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>
              <div className="flex items-center gap-2">
                <input type="color" value={biz[key] || def} onChange={set(key)} data-testid={`color-${key}`} className="w-11 h-10 rounded cursor-pointer border border-gray-200 shrink-0" />
                <input value={biz[key] || ""} placeholder={def} onChange={set(key)} className="w-full min-w-0 rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#8a97a8] -mt-1">These colours style the text and buttons across your public storefront.</p>
        <div>
          <label className="block text-sm font-medium text-[#16243b] mb-1.5">Description</label>
          <textarea value={biz.description} onChange={set("description")} rows={2}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] resize-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#16243b] mb-1.5">Voucher validity (months)</label>
          <input type="number" min="0" value={biz.voucher_validity_months ?? 12} onChange={set("voucher_validity_months")}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20" />
          <p className="text-xs text-[#8a97a8] mt-1">New vouchers expire after this many months. Default 12. Set to 0 for immediate expiry.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#16243b] mb-1.5">Voucher terms & conditions</label>
          <textarea value={biz.terms} onChange={set("terms")} rows={3}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] resize-none" />
        </div>

        <UpgradeLock feature="advanced_design" hint="Background image, fonts, banner layouts, card styles and extra page sections."><DesignEditor design={biz.design || {}} onChange={(design) => setBiz({ ...biz, design })} /></UpgradeLock>

        <UpgradeLock feature="video_messages" compact>
        <div className="border-t border-gray-100 pt-4 flex items-start justify-between gap-4" data-testid="video-messages-setting">
          <div>
            <h4 className="font-semibold text-[#16243b] text-sm">Video gift messages</h4>
            <p className="text-xs text-[#8a97a8] mt-1">Let buyers record or upload a 30-second video that the recipient watches when opening their voucher.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#16243b] shrink-0">
            <input type="checkbox" checked={biz.video_messages_enabled !== false} onChange={(e) => setBiz({ ...biz, video_messages_enabled: e.target.checked })} data-testid="video-messages-toggle" /> Enabled
          </label>
        </div>
        </UpgradeLock>

        <UpgradeLock feature="promo_banner"><PromoEditor promo={biz.promo_banner || {}} onChange={(promo_banner) => setBiz({ ...biz, promo_banner })} /></UpgradeLock>

        <div className="border-t border-gray-100 pt-4">
          <h4 className="font-semibold text-[#16243b] text-sm mb-1">SEO & social sharing</h4>
          <p className="text-xs text-[#8a97a8] mb-3">Controls your storefront's page title, search description and the preview image shown when your store link is shared.</p>
          <div className="space-y-4">
            <Field label="SEO page title" value={biz.seo_title} onChange={set("seo_title")} placeholder={`${biz.name} — Gift Vouchers`} testid="storefront-seo-title" />
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">SEO / share description</label>
              <textarea value={biz.seo_description || ""} onChange={set("seo_description")} rows={2} placeholder={biz.description} data-testid="storefront-seo-description"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] resize-none" />
            </div>
            <ImageUpload label="Social share image (recommended 1200×630)" value={biz.share_image} onChange={(v) => setBiz({ ...biz, share_image: v })} />
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <h4 className="font-semibold text-[#16243b] text-sm mb-3">Your website & social links</h4>
          <div className="space-y-3">
            <Field label="Website URL" value={biz.website} onChange={set("website")} placeholder="https://yourbusiness.co.uk" testid="storefront-website" />
            {(biz.social_links || []).map((s, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`biz-social-row-${i}`}>
                <select value={s.platform || "instagram"} onChange={(e) => setBiz({ ...biz, social_links: biz.social_links.map((x, xi) => xi === i ? { ...x, platform: e.target.value } : x) })}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm capitalize bg-white">
                  {["instagram", "facebook", "twitter", "linkedin", "youtube"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input value={s.url || ""} placeholder="https://…" data-testid={`biz-social-url-${i}`} onChange={(e) => setBiz({ ...biz, social_links: biz.social_links.map((x, xi) => xi === i ? { ...x, url: e.target.value } : x) })}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                <button type="button" onClick={() => setBiz({ ...biz, social_links: biz.social_links.filter((_, xi) => xi !== i) })} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
              </div>
            ))}
            <button type="button" data-testid="add-biz-social" onClick={() => setBiz({ ...biz, social_links: [...(biz.social_links || []), { platform: "instagram", url: "" }] })}
              className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1"><Plus size={14} /> Add social link</button>
          </div>
        </div>

        <button disabled={saving} data-testid="storefront-save-btn" className="gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
      </form>

      {/* Live preview */}
      <div className="lg:sticky lg:top-6 self-start">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-[#16243b]">Preview</p>
          <a href={`/store/${biz.slug}`} target="_blank" rel="noreferrer" className="text-sm text-[#1fb0b0] flex items-center gap-1 hover:underline">Open <ExternalLink size={14} /></a>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.1)]" data-testid="storefront-preview"
          style={{ fontFamily: biz.design?.body_font ? `"${biz.design.body_font}"` : undefined, background: biz.design?.bg_color || "#f6f8fb",
            backgroundImage: biz.design?.bg_image ? `linear-gradient(rgba(0,0,0,${(biz.design.bg_overlay || 0) / 100}),rgba(0,0,0,${(biz.design.bg_overlay || 0) / 100})), url("${biz.design.bg_image}")` : undefined, backgroundSize: "cover" }}>
          <div className={`p-8 relative ${biz.design?.hero_layout === "left" || biz.design?.hero_layout === "split" ? "text-left" : "text-center"}`} style={{ background: biz.brand_color }}>
            {biz.logo_url && <img src={biz.logo_url} alt="" className={`h-20 mb-3 object-contain ${biz.design?.hero_layout === "left" || biz.design?.hero_layout === "split" ? "" : "mx-auto"}`} />}
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: biz.design?.heading_font ? `"${biz.design.heading_font}"` : undefined }}>{biz.name}</h3>
            <p className="text-white opacity-85 text-sm mt-1">{biz.description}</p>
          </div>
          <div className="p-6">
            <div className={`bg-white p-5 text-center text-sm ${biz.design?.card_corners === "square" ? "" : biz.design?.card_corners === "pill" ? "rounded-[1.5rem]" : "rounded-xl"}`} style={{ color: biz.text_color || "#16243b" }}>
              Your vouchers appear here
              <div className="mt-3"><span className="inline-block rounded-full px-5 py-2 text-sm font-medium" style={{ background: biz.button_color || "#2ec9c9", color: biz.button_text_color || "#ffffff" }}>View &amp; buy</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, prefix, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>
      <div className="flex items-center">
        {prefix && <span className="text-[#8a97a8] text-sm mr-1">{prefix}</span>}
        <input value={value || ""} onChange={onChange} placeholder={placeholder} data-testid={testid}
          className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20" />
      </div>
    </div>
  );
}
